/* ============================================
   Globdrop — Scoring System (Pure Functions)
   ============================================ */

/**
 * Calculate points for a single round.
 *
 * Mirrors the original formula from the legacy game but generalized.
 *
 * Original: points = ((count+1)/2) * (50 - dist) * 2
 *
 * @param distanceKm - Real distance from target in km
 * @param remainingSeconds - Time left on the clock
 * @param maxDistance - Distance threshold beyond which 0 points are awarded (default: 50)
 * @returns Points earned (0 if distance exceeds threshold or time ran out)
 */
export function calculatePoints(
  distanceKm: number,
  remainingSeconds: number,
  maxDistance: number = 50
): number {
  if (distanceKm >= maxDistance || remainingSeconds <= 0) return 0;

  const timeMultiplier = (remainingSeconds + 1) / 2;
  // Precision as a percentage of the allowed distance (0 to 1)
  const precisionFactor = Math.max(0, (maxDistance - distanceKm) / maxDistance);
  const precisionScore = precisionFactor * 100; // Standardized score up to 100
  
  const raw = timeMultiplier * precisionScore;

  return Math.max(0, Math.round(raw));
}

/**
 * Calculate the maximum possible points for a game.
 *
 * Assumes perfect precision (0 km distance) and maximum time remaining.
 *
 * @param rounds - Number of rounds
 * @param timePerRound - Seconds per round
 * @param maxDistance - Distance threshold
 * @returns Maximum theoretical points
 */
export function maxPossiblePoints(
  rounds: number,
  timePerRound: number = 10,
  maxDistance: number = 50
): number {
  return rounds * calculatePoints(0, timePerRound, maxDistance);
}

import { getLocale } from './i18n.ts';
import type { DistanceUnit, LText } from '../types/scenario.ts';

/**
 * Get the achievement level based on total score.
 *
 * @param totalPoints - Player's total score
 * @param maxPoints - Maximum possible score
 * @param levels - Array of level titles (from worst to best)
 * @returns The level title the player achieved
 */
export function getLevel(
  totalPoints: number,
  maxPoints: number,
  levels: LText[]
): LText {
  if (levels.length === 0) return '';
  if (maxPoints <= 0) return levels[0] ?? '';

  const ratio = Math.min(totalPoints / maxPoints, 1);
  const index = Math.min(
    Math.floor(ratio * levels.length),
    levels.length - 1
  );

  return levels[index] ?? '';
}

/**
 * Format a distance for display.
 *
 * @param distance - Distance in the scenario's unit
 * @param unit - 'km' or 'mi' (the US and fictional maps use miles)
 * @returns Formatted string (e.g., "12 km", "0,5 km", "140 mi")
 */
export function formatDistance(distance: number, unit: DistanceUnit = 'km'): string {
  const locale = getLocale();
  
  if (distance < 1) {
    return `${distance.toLocaleString(locale, { maximumFractionDigits: 1 })} ${unit}`;
  }
  return `${Math.round(distance).toLocaleString(locale)} ${unit}`;
}

/**
 * Format points for display.
 *
 * @param points - Number of points
 * @returns Formatted string with appropriate locale (e.g., "3.250")
 */
export function formatPoints(points: number): string {
  return points.toLocaleString(getLocale());
}
